import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Kngdm free gift: keeps the free Mystery T-Shirt in the cart in sync with the
 * BUY3GET4 automatic discount (buy 3 from Christian Tee Sale!, get 1 free).
 *
 * - Adds 1 free tee per 3 qualifying items in the cart.
 * - Removes or reduces it when qualifying items are removed.
 * - Corrects the quantity if the shopper changes the free line by hand.
 * - Never touches the cart if the collection lookup fails.
 *
 * The discount itself still lives in Shopify; this only puts the item in the cart.
 */
const FREE_VARIANT_ID = 67739206615340;
const COLLECTION_HANDLE = 'frontpage';
const BUY_QTY = 3;
const CACHE_KEY = 'kngdm-free-gift-ids-v2';
const CACHE_MS = 60 * 60 * 1000;
let cachedQualifyingIds = null;

let running = false;
let queued = false;
let internal = false;
let timer = null;

/** @returns {Promise<Set<number>>} product ids in the qualifying collection */
async function getQualifyingIds() {
  if (cachedQualifyingIds) return cachedQualifyingIds;
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.at < CACHE_MS && cached.ids.length) {
      cachedQualifyingIds = new Set(cached.ids);
      return cachedQualifyingIds;
    }
  } catch (e) {}

  const ids = [];
  for (let page = 1; page <= 10; page++) {
    const res = await fetch(`/collections/${COLLECTION_HANDLE}/products.json?limit=250&page=${page}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) break;
    const data = await res.json();
    if (!data.products || !data.products.length) break;
    data.products.forEach((p) => ids.push(p.id));
    if (data.products.length < 250) break;
  }

  if (ids.length) {
    cachedQualifyingIds = new Set(ids);
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), ids }));
    } catch (e) {}
    return cachedQualifyingIds;
  }
  // A failed/empty collection lookup must not be cached; retry next time.
  return new Set();
}

async function getCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart request failed (${res.status})`);
  return res.json();
}

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.description || json.message || `Cart request failed (${res.status})`);
  return json;
}

function sectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId);
  return [...new Set(ids.filter(Boolean))];
}

/** Tells the theme the cart changed so the drawer, cart page and count icon re-render. */
function announce(cart, sections, desired) {
  const line = cart.items.find((i) => i.variant_id === FREE_VARIANT_ID);
  const deferred = CartLinesUpdateEvent.createPromise();

  internal = true;
  try {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: desired ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: line ? line.key : '', quantity: desired }],
        promise: deferred.promise,
      })
    );
  } finally {
    internal = false;
  }

  let cartObj = cart;
  try {
    cartObj = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
  } catch (e) {}

  deferred.resolve({
    cart: cartObj,
    detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'free-gift', didError: false },
  });
}

async function syncOnce() {
  // Run independent requests concurrently instead of making the customer
  // wait for the collection lookup to finish before fetching their cart.
  const [ids, cart] = await Promise.all([getQualifyingIds(), getCart()]);
  if (!ids.size) return;
  let qualifying = 0;
  const freeLines = [];
  for (const item of cart.items) {
    if (item.variant_id === FREE_VARIANT_ID) freeLines.push(item);
    else if (ids.has(item.product_id)) qualifying += item.quantity;
  }

  const desired = Math.floor(qualifying / BUY_QTY);
  const current = freeLines.reduce((n, i) => n + i.quantity, 0);
  if (desired === current && freeLines.length <= 1) return;

  const sections = sectionIds().join(',');
  const extra = sections ? { sections, sections_url: window.location.pathname } : {};

  let result;
  if (freeLines.length) {
    const updates = {};
    freeLines.forEach((line, i) => {
      updates[line.key] = i === 0 ? desired : 0;
    });
    result = await post('/cart/update.js', { updates, ...extra });
    // /cart/update.js returns the complete cart. Skip a redundant /cart.js.
    announce(result, result.sections, desired);
  } else {
    result = await post('/cart/add.js', { items: [{ id: FREE_VARIANT_ID, quantity: desired }], ...extra });
    // /cart/add.js returns added line(s), so read the full cart once here.
    announce(await getCart(), result.sections, desired);
  }
}

async function runSync() {
  if (running) {
    queued = true;
    return;
  }
  running = true;
  try {
    do {
      queued = false;
      await syncOnce();
    } while (queued);
  } catch (e) {
    console.warn('[free-gift]', e);
  } finally {
    running = false;
  }
}

function scheduleSync() {
  clearTimeout(timer);
  timer = setTimeout(runSync, 80);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (internal) return;
  const promise = event.promise;
  if (promise && typeof promise.then === 'function') {
    promise
      .then((result) => {
        if (!(result && result.detail && result.detail.didError)) scheduleSync();
      })
      .catch(() => {});
  } else {
    scheduleSync();
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleSync);
else scheduleSync();

window.addEventListener('pageshow', (e) => {
  if (e.persisted) scheduleSync();
});
